import {keepPreviousData, useQuery} from '@tanstack/react-query'
import {fetchClient} from '../../../shared/api'
import {usdLabel, uzsOf} from './currency'

/* =========================================================================
   WMS · Доставка — клиент API (SCRUM-565, «Активные доставки» — SCRUM-566).
   Контракты: SCRUM-558 (заявки «К доставке», counters), SCRUM-559
   (wms-delivery-task-scrum-559.md — задания D-xx, новые ключи машин),
   SCRUM-563 (PDF «Список к доставке», путевой лист), SCRUM-557 (геозоны),
   SCRUM-561 (wms-delivery-driver-scrum-561.md — что меняет приложение
   водителя: «Не доставлено» диспетчером, `position`, фото, `done_by`),
   wms-route-handover.md («Сдача на складе», «Касса»; маршрут закрывается сам — wms-frontend-changes-2026-10-27.md).

   Схема codegen эти эндпоинты не описывает — сырой fetchClient с локальными
   типами, как и в остальных wms/pages/*.tsx.
   ========================================================================= */

export type LatLng = [number, number]

export interface ZoneRef {
  id: number
  name: string
  color: string
}
export interface WindowRef {
  id: number
  start_time: string
  end_time: string
  label: string
}

/** Строка `GET /delivery/requests/` — «Готов к доставке» и ни в одном задании. */
export interface ToDeliverRequest {
  id: number
  schedule_number: string
  client: {code: string; name: string; phone: string}
  phone?: string
  date: string
  /** У некоторых сборок бэка окно лежит наверху, у других — только в delivery. */
  window?: WindowRef | string | null
  places_count: number
  weight: number | string
  volume: number | string
  /** USD (цена из 1С); на экран — через `currency_rate`. */
  amount: string
  /** Текущий курс USD→UZS, одинаковый у всех строк; null — курса ещё нет. */
  currency_rate?: string | null
  pay_on_delivery: boolean
  comment?: string
  is_overdue?: boolean
  delivery: {
    lat: number | null
    lng: number | null
    address?: string
    address_text?: string
    comment?: string
    recipient_phone?: string
    zone: ZoneRef | null
    window?: WindowRef | null
    pay_on_delivery?: boolean
  }
}

export interface Geozone extends ZoneRef {
  polygon: LatLng[]
  is_active: boolean
  sort_order: number
}

export interface TaskRef {
  id: number
  number: string
  status: TaskStatus
  stops_left?: number
}

export interface DeliveryVehicle {
  id: number
  name: string
  plate: string
  volume_m3: number
  max_load_kg: number
  is_active: boolean
  default_driver: {id: number; full_name: string; is_available: boolean} | null
  in_use?: boolean
  /** Задание `loading` / `on_route` этой машины; null — свободна. */
  current_task?: TaskRef | null
  queued_tasks?: number
}

export interface DriverOption {
  id: number
  full_name: string | null
  first_name: string
  last_name: string
}

export type TaskStatus =
  | 'scheduled'
  | 'queued'
  | 'loading'
  | 'on_route'
  | 'handover'
  | 'done'
  | 'cancelled'

/** Деталь задания (раздел 6) — здесь нужны только поля для тоста и перехода. */
export interface DeliveryTask {
  id: number
  number: string
  status: TaskStatus
  status_label: string
  date: string
  queue: {position: number; behind: TaskRef | null} | null
  capacity?: {over_weight: boolean; over_volume: boolean}
}

export interface CreateTaskBody {
  vehicle: number
  driver: number
  date: string
  stops: number[]
  return_to_warehouse: boolean
  comment: string
}

export interface DeliveryCounters {
  to_deliver: number
  active_tasks: number
}

/* ── Сырой клиент ───────────────────────────────────────────────────────── */

type RawRes<T> = {data?: T; error?: unknown; response?: Response}
const rawGet = fetchClient.GET as unknown as (
  path: string,
  opts?: {parseAs?: 'blob'}
) => Promise<RawRes<unknown>>
const rawPost = fetchClient.POST as unknown as (
  path: string,
  opts: {body?: unknown}
) => Promise<RawRes<unknown>>
const rawPatch = fetchClient.PATCH as unknown as (
  path: string,
  opts: {body?: unknown}
) => Promise<RawRes<unknown>>

type ApiError = {detail?: string; errors?: {error?: string; message?: string}[]}
export const apiErrors = (e: unknown): {error?: string; message?: string}[] => {
  const err = e as ApiError | undefined
  if (!err) return []
  if (err.errors?.length) return err.errors
  return err.detail ? [{message: err.detail}] : []
}

/** Плоский массив или DRF-пагинация — принимаем оба. */
const listOf = <T>(data: unknown): T[] =>
  Array.isArray(data) ? (data as T[]) : (data as {results?: T[]})?.results ?? []

/* ── Запросы ────────────────────────────────────────────────────────────── */

export const DELIVERY_KEY = ['wms', 'delivery'] as const

/** Все готовые к доставке заявки; дата/зона/поиск фильтруются на странице. */
export const useToDeliverRequests = () =>
  useQuery({
    queryKey: [...DELIVERY_KEY, 'requests', 'planning'],
    queryFn: async (): Promise<ToDeliverRequest[]> => {
      const {data, error} = await rawGet('/wms/delivery/requests/')
      if (error) throw error
      return listOf<ToDeliverRequest>(data)
    },
    refetchInterval: 60_000,
  })

/** Бейджи сайдбара «К доставке N» / «Активные доставки N». */
export const useDeliveryCounters = (enabled: boolean) =>
  useQuery({
    queryKey: [...DELIVERY_KEY, 'counters'],
    queryFn: async (): Promise<DeliveryCounters> => {
      const {data, error} = await rawGet('/wms/delivery/counters/')
      if (error) throw error
      return data as DeliveryCounters
    },
    enabled,
    refetchInterval: 30_000,
  })

export const useDeliveryVehicles = () =>
  useQuery({
    queryKey: ['wms', 'vehicles', 'delivery'],
    queryFn: async (): Promise<DeliveryVehicle[]> => {
      const {data, error} = await rawGet('/wms/vehicles/?is_active=true')
      if (error) throw error
      return listOf<DeliveryVehicle>(data)
    },
    refetchInterval: 60_000,
  })

/** Склад по умолчанию (Tashkent Trade Center) — пока не ответил
 *  `/wms/delivery/warehouse/`; совпадает с дефолтом бэка (SCRUM-577). */
export const DEFAULT_WAREHOUSE_POINT: LatLng = [41.2385, 69.229]

export const WAREHOUSE_POINT_KEY = [...DELIVERY_KEY, 'warehouse'] as const

/** Точка склада для карт «Доставки» (SCRUM-578). Читаем не `/wms/settings/service/`
 *  — туда пускают только Админа, а карты смотрит и Диспетчер. */
export const useWarehousePoint = (): LatLng => {
  const q = useQuery({
    queryKey: WAREHOUSE_POINT_KEY,
    queryFn: async (): Promise<LatLng> => {
      const {data, error} = await rawGet('/wms/delivery/warehouse/')
      if (error) throw error
      const {lat, lng} = data as {lat: number; lng: number}
      return [lat, lng]
    },
    staleTime: 5 * 60_000,
  })
  return q.data ?? DEFAULT_WAREHOUSE_POINT
}

/** `GET /common/currency-rate/` — для строки «Курс 1 $ = …», когда строк нет. */
export interface CurrencyRate {
  currency: string
  base: string
  rate: string
  effective_from: string
  updated_at: string
}

/** null — курса нет (`503 rate_unavailable`) или запрос упал. */
export const useCurrencyRate = () =>
  useQuery({
    queryKey: ['common', 'currency-rate'],
    queryFn: async (): Promise<CurrencyRate | null> => {
      const {data, error} = await rawGet('/common/currency-rate/')
      if (error || !data) return null
      return data as CurrencyRate
    },
    staleTime: 5 * 60_000,
    retry: false,
  })

export const useDeliveryGeozones = () =>
  useQuery({
    queryKey: ['wms', 'geozones', 'list'],
    queryFn: async (): Promise<Geozone[]> => {
      const {data, error} = await rawGet('/wms/geozones/')
      if (error) throw error
      return listOf<Geozone>(data)
    },
    staleTime: 5 * 60_000,
  })

export const useDrivers = (enabled: boolean) =>
  useQuery({
    queryKey: ['wms', 'employees', 'drivers'],
    enabled,
    queryFn: async (): Promise<DriverOption[]> => {
      const {data, error} = await rawGet('/wms/employees/?role=driver&is_active=true&limit=100')
      if (error) throw error
      return listOf<DriverOption>(data)
    },
  })

export const driverName = (d: DriverOption) =>
  d.full_name || [d.first_name, d.last_name].filter(Boolean).join(' ') || `#${d.id}`

/** `POST /delivery/tasks/` — 201 с деталью задания, иначе бросает конверт ошибок. */
export const createDeliveryTask = async (body: CreateTaskBody): Promise<DeliveryTask> => {
  const {data, error} = await rawPost('/wms/delivery/tasks/', {body})
  if (error) throw error
  return data as DeliveryTask
}

/** Больше `ids` бэк не принимает (`400 ids_invalid`). */
export const PRINT_MAX_IDS = 500

/** Фильтры «К доставке» для печати без выбора — как у `GET /delivery/requests/`. */
export interface DeliveryListFilters {
  date?: string
  /** id геозоны или `none` — «Вне зон». */
  zone?: number | 'none'
  q?: string
}

/** Ошибка скачивания PDF: статус и JSON-тело в обычном конверте. */
export type PdfError = Error & {status?: number; body?: unknown}

const fetchPdf = async (path: string, name: string): Promise<string> => {
  const {data, error, response} = await rawGet(path, {parseAs: 'blob'})
  if (error || !data) {
    const e = new Error(name) as PdfError
    e.status = response?.status
    e.body = error
    throw e
  }
  return URL.createObjectURL(data as Blob)
}

/** Текст ошибки PDF с сервера (`task_cancelled`, `detail_nothing_to_print`…) или fallback. */
export const pdfErrorText = (e: unknown, fallback: string) =>
  apiErrors((e as PdfError)?.body)
    .map((x) => x.message || x.error)
    .filter(Boolean)
    .join(' ') || fallback

/**
 * `GET /delivery/requests/print.pdf` (SCRUM-563) — «Печать списка»: выбранные
 * заявки (`ids`, до 500) или текущие фильтры страницы.
 * Возвращает object-URL PDF; вызывающий открывает его и сам освобождает.
 */
export const fetchDeliveryListPdf = (sel: {ids: number[]} | {filters: DeliveryListFilters}) => {
  const qs = new URLSearchParams()
  if ('ids' in sel) qs.set('ids', sel.ids.join(','))
  else
    Object.entries(sel.filters).forEach(([k, v]) => {
      if (v !== undefined && v !== '') qs.set(k, String(v))
    })
  const query = qs.toString()
  return fetchPdf(`/wms/delivery/requests/print.pdf${query ? `?${query}` : ''}`, 'print_failed')
}

/* ── Хелперы строк ──────────────────────────────────────────────────────── */

export const reqAddress = (r: ToDeliverRequest) =>
  r.delivery?.address || r.delivery?.address_text || ''

export const reqWindow = (r: ToDeliverRequest): string => {
  if (r.delivery?.window?.label) return r.delivery.window.label
  if (typeof r.window === 'string') return r.window
  return r.window?.label ?? ''
}

/** Начало окна «10:00» для сортировки; без окна — в конец дня. */
export const reqWindowStart = (r: ToDeliverRequest): string =>
  r.delivery?.window?.start_time ||
  (typeof r.window === 'object' && r.window ? r.window.start_time : '') ||
  reqWindow(r).slice(0, 5) ||
  '99:99'

export const reqPhone = (r: ToDeliverRequest) => r.phone || r.client?.phone || ''
export const reqComment = (r: ToDeliverRequest) => r.delivery?.comment || r.comment || ''
export const reqWeight = (r: ToDeliverRequest) => Number(r.weight) || 0
export const reqVolume = (r: ToDeliverRequest) => Number(r.volume) || 0
/** Наличные курьеру в сумах по курсу строки (без курса — в USD): сумма есть
 *  только у неоплаченной заявки. */
export const reqCash = (r: ToDeliverRequest) =>
  r.pay_on_delivery ? uzsOf(r.amount, r.currency_rate) ?? (Number(r.amount) || 0) : 0
/** «3 212 500 сум» / «$ 250»; без наличных — пусто. */
export const reqCashLabel = (r: ToDeliverRequest, unit?: string) =>
  r.pay_on_delivery ? usdLabel(r.amount, r.currency_rate, unit) : ''
export const reqPoint = (r: ToDeliverRequest): LatLng | null =>
  r.delivery?.lat != null && r.delivery?.lng != null ? [r.delivery.lat, r.delivery.lng] : null

/* =========================================================================
   «Активные доставки» (SCRUM-566): список заданий, деталь и действия.
   Схемы — DeliveryTaskRow / DeliveryTaskDetail из swagger (SCRUM-559 + 561).
   Суммы приходят строками («125000.00»), как и везде в WMS API.
   ========================================================================= */

export interface PersonRef {
  id: number
  full_name: string
}

export interface TaskVehicle {
  id: number
  name: string
  plate: string
  max_load_kg: number
  volume_m3: number
  /** false — машину выключили уже после создания задания. */
  is_active: boolean
}

/** Суммы маршрута. `to_collect` — USD, остальное — уже в сумах (SCRUM-584). */
export interface TaskCash {
  /** Все точки, кроме «Не доставлено»; живое, пока не зафиксировано. USD. */
  to_collect: string | null
  /** `to_collect` в сумах по курсу маршрута — как в путевом листе; null — курса нет. */
  to_collect_uzs?: string | null
  /** Что водитель отметил в приложении как полученное. */
  collected: string | null
  /** При приёме кассой: собрано по доставленным точкам. */
  expected: string | null
  /** При приёме кассой: сколько касса насчитала. */
  received: string | null
  difference: string | null
  has_difference: boolean
  /** null — касса ещё не приняла наличные. */
  accepted_at?: string | null
  accepted_by?: PersonRef | null
}

/** Что касса и контролёр ещё должны маршруту в «Сдаче на складе». */
export type HandoverState = 'waiting' | 'accepted' | 'not_required'

export interface CarPosition {
  lat: number
  lng: number
  /** Когда телефон водителя взял точку. */
  at: string
}

export type StopStatus = 'pending' | 'arrived' | 'delivered' | 'failed'

export interface DeliveryTaskRow {
  id: number
  number: string
  status: TaskStatus
  status_label: string
  date: string
  is_today: boolean
  vehicle: TaskVehicle | null
  driver: (PersonRef & {phone?: string}) | null
  stops_count: number
  places_total: number
  loaded_places: number
  ready_to_depart: boolean
  not_found_places: number
  weight_kg: number
  volume_m3: number
  /** Зоны в порядке маршрута; null — «Вне зон». */
  zones: (ZoneRef | null)[]
  progress: {delivered: number; failed: number; left: number}
  next_stop: {
    id: number
    order: number
    schedule_number: string
    client_name: string
    window: string
  } | null
  cash: TaskCash
  /** Снимок курса маршрута (или текущий, пока снимка нет); null — курса нет. */
  currency_rate?: string | null
  code_locked_stops: number
  position: CarPosition | null
  queue: {position: number; behind: TaskRef | null} | null
  comment: string
  return_to_warehouse: boolean
  created_at: string
  loaded_at: string | null
  started_at: string | null
  /** Когда водитель нажал «Завершить маршрут». */
  handover_at?: string | null
  /** Закрытие маршрута водителем. */
  finished_at: string | null
  cancelled_at: string | null
  /** null — до «Завершить маршрут». */
  handover?: {cash: HandoverState; returns: HandoverState} | null
}

export interface TaskStop {
  id: number
  order: number
  status: StopStatus
  status_label: string
  schedule: {
    id: number
    schedule_number: string
    client: {code: string; name: string; phone: string}
    /** Телефон получателя, если указан, иначе клиента. */
    phone: string
    date: string
    places_count: number
    weight: number
    volume: number
    cells: string[]
    delivery: {
      lat: number | null
      lng: number | null
      address: string
      comment: string
      recipient_phone: string
      zone: ZoneRef | null
      window: WindowRef | null
      pay_on_delivery: boolean
      returned_once?: boolean
      return_incomplete?: boolean
    }
  }
  /** false — «Оплачено», собирать нечего. */
  pay_on_delivery: boolean
  /** USD — переводим по `currency_rate` задания. */
  cash_expected: string | null
  /** Уже в сумах — как отметил водитель. */
  cash_received: string | null
  fail_reason: string | null
  fail_reason_label: string | null
  fail_comment: string | null
  arrived_at: string | null
  done_at: string | null
  client_code_confirmed: boolean
  code_locked: boolean
  code_locked_at: string | null
  comment: string
  done_by: PersonRef | null
  marked_by: 'driver' | 'dispatcher' | null
  load: {places: number; loaded: number; not_found: number} | null
  /** Подписанные ссылки живут час — берём из свежего ответа, не храним. */
  photos: {id: number; url: string; created_at: string}[]
}

export interface DeliveryTaskDetail extends DeliveryTaskRow {
  capacity: {
    weight_kg: number
    max_kg: number
    volume_m3: number
    max_m3: number
    over_weight: boolean
    over_volume: boolean
  } | null
  created_by: PersonRef | null
  /** Водитель, закрывший маршрут в приложении. */
  closed_by: PersonRef | null
  cancelled_by: PersonRef | null
  cancel_reason: string
  stops: TaskStop[]
  loading: {task_id: number; task_number: string; state: string; performer: PersonRef | null} | null
  returns: {
    task_id: number
    task_number: string
    return_reason: string
    state: string
    places: number
    returned: number
    not_found: number
  }[]
}

/** `stats` списка — считает все задания, без учёта фильтров. */
export type DeliveryTaskStats = Partial<Record<TaskStatus | 'active', number>> & {
  stops_left?: number
  delivered_today?: number
  failed_today?: number
  cash_with_drivers?: string | number
  code_locked?: number
  [k: string]: unknown
}

export interface DeliveryTaskList {
  count: number
  stats: DeliveryTaskStats
  results: DeliveryTaskRow[]
}

export const FAIL_REASONS = [
  'client_absent',
  'no_answer',
  'wrong_address',
  'refused',
  'reschedule',
  'other',
] as const
export type FailReason = typeof FAIL_REASONS[number]

/** `GET /delivery/tasks/` с фильтрами (status, vehicle, q …). */
export const fetchDeliveryTasks = async (
  params: Record<string, string>
): Promise<DeliveryTaskList> => {
  const qs = new URLSearchParams({limit: '200', ...params})
  const {data, error} = await rawGet(`/wms/delivery/tasks/?${qs}`)
  if (error) throw error
  const d = data as Partial<DeliveryTaskList> | DeliveryTaskRow[]
  if (Array.isArray(d)) return {count: d.length, stats: {}, results: d}
  return {count: d.count ?? 0, stats: d.stats ?? {}, results: d.results ?? []}
}

/** Список «Активных доставок»; опрос каждые 30 с — прогресс, наличные, GPS. */
export const useDeliveryTasks = (status: TaskStatus | null, q: string) =>
  useQuery({
    queryKey: [...DELIVERY_KEY, 'tasks', status ?? 'all', q],
    queryFn: () => {
      const params: Record<string, string> = {}
      if (status) params.status = status
      if (q) params.q = q
      return fetchDeliveryTasks(params)
    },
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  })

export const taskKey = (id: number) => [...DELIVERY_KEY, 'task', id] as const

/** Деталь задания — тот же таймер, чтобы подтягивать новые фото и статусы точек. */
export const useDeliveryTask = (id: number | null) =>
  useQuery({
    queryKey: taskKey(id ?? 0),
    enabled: id != null,
    queryFn: async (): Promise<DeliveryTaskDetail> => {
      const {data, error} = await rawGet(`/wms/delivery/tasks/${id}/`)
      if (error) throw error
      return data as DeliveryTaskDetail
    },
    refetchInterval: 30_000,
  })

const taskAction =
  <B>(suffix: string, method: 'post' | 'patch' = 'post') =>
  async (id: number, body: B): Promise<DeliveryTaskDetail> => {
    const fn = method === 'patch' ? rawPatch : rawPost
    const {data, error} = await fn(`/wms/delivery/tasks/${id}/${suffix}`, {body})
    if (error) throw error
    return data as DeliveryTaskDetail
  }

/** Машина, водитель, комментарий, возврат на склад — до выезда. */
export const patchDeliveryTask = taskAction<
  Partial<{vehicle: number; driver: number; comment: string; return_to_warehouse: boolean}>
>('', 'patch')
export const cancelDeliveryTask = taskAction<{reason?: string}>('cancel/')
export const loadDeliveryTaskNow = taskAction<Record<string, never>>('load-now/')
export const reorderDeliveryTask = taskAction<{stop_ids: number[]}>('reorder/')

export const failDeliveryStop = (
  id: number,
  stopId: number,
  body: {reason: FailReason; comment: string}
) => taskAction<typeof body>(`stops/${stopId}/fail/`)(id, body)
export const removeDeliveryStop = (id: number, stopId: number) =>
  taskAction<Record<string, never>>(`stops/${stopId}/remove/`)(id, {})
export const unlockDeliveryStop = (id: number, stopId: number) =>
  taskAction<Record<string, never>>(`stops/${stopId}/unlock/`)(id, {})

/** `GET /delivery/tasks/{id}/waybill.pdf` — путевой лист; object-URL освобождает вызывающий. */
export const fetchWaybillPdf = (id: number) =>
  fetchPdf(`/wms/delivery/tasks/${id}/waybill.pdf`, 'waybill_failed')

/* ── Касса ──────────────────────────────────────────────────────────────── */

/** Строка `GET /kassa/tasks/` — маршрут, который должен наличные кассе. */
export interface KassaTask {
  id: number
  number: string
  status: TaskStatus
  status_label: string
  /** false — все точки отработаны, но водитель ещё не нажал «Завершить маршрут». */
  driver_finished: boolean
  handover_at: string | null
  date: string
  driver: (PersonRef & {phone?: string}) | null
  vehicle: {name: string; plate: string} | null
  stops_count: number
  delivered: number
  failed: number
  /** Курс маршрута; суммы ниже уже в сумах. */
  currency_rate?: string | null
  cash: {
    expected: string
    items: {
      stop_id: number
      order: number
      schedule_number: string
      client_name: string
      amount: string
    }[]
  }
}

export interface KassaReceipt {
  id: number
  number: string
  status: TaskStatus
  status_label: string
  cash: TaskCash
  currency_rate?: string | null
}

export const KASSA_KEY = ['wms', 'kassa'] as const

/** Маршруты, наличные которых касса ещё не приняла; опрос каждые 30 с. */
export const useKassaTasks = () =>
  useQuery({
    queryKey: [...KASSA_KEY, 'tasks'],
    queryFn: async (): Promise<KassaTask[]> => {
      const {data, error} = await rawGet('/wms/kassa/tasks/')
      if (error) throw error
      return listOf<KassaTask>(data)
    },
    refetchInterval: 30_000,
  })

/** «Принять наличные» — окончательно; `status` в ответе `done`, если ничего не
 *  вернулось, иначе `handover` до конца «Возврата». */
export const acceptTaskCash = async (
  id: number,
  body: {cash_received: string}
): Promise<KassaReceipt> => {
  const {data, error} = await rawPost(`/wms/kassa/tasks/${id}/accept/`, {body})
  if (error) throw error
  return data as KassaReceipt
}
